import { formatCustomFieldValue, isCustomFieldFormat } from '../customField'
import { isNotDefined } from '../isEmpty'
import { DAY } from '../time'
import type { ReportColumnType, ReportColumnValue } from '../../model/report/column'
import type { ReportFormatters } from '../../model/ratio/model'

export type FormatOptions = {
  formatTimestamp: (ms: number) => string
  formatTimeOfDay: (seconds: number) => string
  now: number
  locale?: string
}

export function formatCellValue(
  columnType: ReportColumnType,
  value: ReportColumnValue,
  formatters: ReportFormatters[] | undefined,
  options: FormatOptions
): string | number {
  switch (columnType) {
    case 'string':
      if (isNotDefined(value) || typeof value !== 'string') return '-'
      return value
    case 'number':
      if (isNotDefined(value) || typeof value !== 'number' || isNaN(value)) return '-'
      return formatNumber(value, formatters, options)
    case 'timestamp':
      if (typeof value !== 'number' || isNaN(value)) return '-'
      return formatTimestamp(value, formatters, options)
    case 'boolean':
      if (isNotDefined(value) || typeof value !== 'boolean') return '-'
      return value ? 'Yes' : 'No'
    default:
      return '-'
  }
}

function formatNumber(
  value: string | number,
  formatters: ReportFormatters[] | undefined,
  options: FormatOptions
): string | number {
  const formatter = formatters?.at(0)

  if (!formatter) return value.toLocaleString(options.locale)

  if (isCustomFieldFormat(formatter)) {
    return formatCustomFieldValue(value, formatter)
  }

  if (typeof value === 'string') throw new Error('NumberFormat: Cannot apply numeric format on string')

  switch (formatter) {
    case 'round': {
      const formatted = Math.round(value)
      return formatNumber(formatted, formatters?.slice(1), options)
    }
    case 'ceiling': {
      const formatted = Math.ceil(value)
      return formatNumber(formatted, formatters?.slice(1), options)
    }
    case 'floor': {
      const formatted = Math.floor(value)
      return formatNumber(formatted, formatters?.slice(1), options)
    }
    case 'timeOfDay': {
      const formatted = options.formatTimeOfDay(value)
      return formatNumber(formatted, formatters?.slice(1), options)
    }
    case 'ratio': {
      const formatted = `${ratioPrecision(value)}:1`
      return formatNumber(formatted, formatters?.slice(1), options)
    }
    case 'percentage': {
      const formatted = `${percentagePrecision(value).toLocaleString(options.locale)}%`
      return formatNumber(formatted, formatters?.slice(1), options)
    }
    default:
      throw new Error(`NumberFormat: ${formatter} formatter not supported`)
  }
}

function formatTimestamp(
  value: number,
  formatters: ReportFormatters[] | undefined,
  options: FormatOptions
): string {
  const formatter = formatters?.at(0)

  if (!formatter) {
    return options.formatTimestamp(value)
  }

  switch (formatter) {
    case 'daysSince': {
      const days = Math.round((options.now - value) / DAY)
      return String(days)
    }
    default:
      throw new Error(`TimestampFormat: ${formatter} formatter not supported`)
  }
}

function ratioPrecision(value: number): number {
  if (value < 1) return Math.ceil(value * 100) / 100
  if (value < 10) return Math.ceil(value * 10) / 10
  return Math.ceil(value)
}

function percentagePrecision(value: number): number {
  const percent = value * 100
  if (percent < 1) return toRoundedValue(percent, 2)
  if (percent < 10) return toRoundedValue(percent, 1)
  return toRoundedValue(percent, 0)
}

function toRoundedValue(value: number, precision: number): number {
  return Number(value.toFixed(precision))
}
